import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { gapOpenAt, knotNear, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A block of snow coming down: a lump over a line. */
const SNOW = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5h8l2 4-6 3.5L2 6.5z" fill="currentColor"/><path d="M8 11v3M5 12.5l-1 1.5M11 12.5l1 1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** Two boughs reaching for each other over a gap. */
const GAP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 10.5h5l1-2M14.5 6h-5l-1 2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M5 13.5c1-.6 2-.6 3 0s2 .6 3 0" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** A crow. */
const CROW = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 7.5c2.5-1.6 4.5-1.6 6.5.4 2-2 4-2 6.5-.4-2 .2-3.6 1.4-4.6 3.2L8 9.6l-1.9 1.1C5.1 8.9 3.5 7.7 1.5 7.5z" fill="currentColor"/></svg>';
/** Ceda's hands on a bandage. */
const BANDAGE = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="5.5" width="12" height="5" rx="1.4" fill="currentColor"/><path d="M5 5.5v5M8 5.5v5M11 5.5v5" stroke="#141218" stroke-width="1"/></svg>';
/** The bark swelling. */
const SWELL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 12.5h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M4 12.5c0-3.4 1.8-6 4-6s4 2.6 4 6z" fill="currentColor"/><path d="M7 8.5l1 1.5 1-1" stroke="#141218" stroke-width="0.9" fill="none"/></svg>';
/** A bud on its twig. */
const BUD = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 14.5V8" stroke="currentColor" stroke-width="1.5"/><path d="M8 1.5c2.6 1.8 3.2 4.6 0 7-3.2-2.4-2.6-5.2 0-7z" fill="currentColor"/></svg>';
/** The frost that climbs: an ice crystal. */
const FROST = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M2.4 4.75l11.2 6.5M2.4 11.25l11.2-6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M6.4 2.6 8 4.2l1.6-1.6M6.4 13.4 8 11.8l1.6 1.6" fill="none" stroke="currentColor" stroke-width="1.1"/></svg>';
/** A knot: a band round a branch. */
const KNOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8h13" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/><ellipse cx="8" cy="8" rx="2.2" ry="4.4" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
/** Notches: three bars. */
const NOTCH = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3" width="13" height="10" rx="1.5" fill="currentColor"/><path d="M5 5.5v5M8 5.5v5M11 5.5v5" stroke="#141218" stroke-width="1.5"/></svg>';

/**
 * The Grandsire's crown on screen (docs/sendas/druida.md, chapter 4), inside a path's own plate (game/senda-hud.ts)
 * and over the board; a phone's plate kept short:
 *
 * - the boughs (`boughs`): the snow (red while a block is coming down, its seconds), the next gap (the boughs touching:
 *   cross, its seconds; apart: when they meet), the crows on the hero.
 * - the hollow (`hollow`): Ceda's bandage (its share; stopped by a bite), the bark swelling (by whom and its seconds:
 *   tread it; by the hero: move), Hob's blows left.
 * - the sap (`sap`, the test): the crown at work (its buds open of three, holding, cracked; the next pulse), the other
 *   crown (frozen, running), the frost (climbing; on a knot: strike it; its claw: get out), Hob, and the notches' key.
 *
 * Over the board, one button: a tap by a knot of the crown at work walks the hero there and offers to close or open
 * it; the button sends it (the hero stands still there, its Raíz, its share filling the ring). Plain DOM, written only
 * when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class CopaHud {
  readonly panel: HTMLElement;
  private kind: 'boughs' | 'hollow' | 'sap';
  private v = new THREE.Vector3();
  private rows: Record<string, { row: HTMLElement; text: HTMLElement }> = {};
  private offerEl: HTMLButtonElement | null = null;
  private offerLabel: HTMLElement | null = null;
  private offerAt = -1;
  private offerPoint = new THREE.Vector3();

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.winter;
    this.kind = r?.kind ?? 'boughs';
    const rows: HTMLElement[] = [];
    const row = (id: string, ico: string, label = ''): HTMLElement => {
      const text = h('span.al-wave');
      const el = h(`div.bh-row.cp-${id}`, {}, h('span.bh-ico', { html: ico }), label ? h('span.bh-label', {}, label) : '', text);
      this.rows[id] = { row: el, text };
      rows.push(el);
      return el;
    };
    if (this.kind === 'boughs') {
      row('snow', SNOW);
      row('gap', GAP);
      row('crows', CROW);
    } else if (this.kind === 'hollow') {
      row('bandage', BANDAGE, t('senda.copa.ceda'));
      row('swell', SWELL);
    } else {
      row('low', BUD, t('senda.copa.low'));
      row('high', BUD, t('senda.copa.high'));
      row('frost', FROST, t('senda.copa.frost'));
      row('swell', SWELL);
      rows.push(h('div.bh-row.cp-key', {}, h('span.bh-ico', { html: NOTCH }), h('span.al-wave', {}, t('senda.copa.key'))));
    }
    this.panel = h(`div.bh.cp-panel.cp-${this.kind}`, {}, ...rows);
    if (this.kind === 'sap') this.buildOffer();
  }

  /** The one «Cerrar»/«Abrir» button over the board. */
  private buildOffer(): void {
    const label = h('small.cp-offer-label', {}, t('senda.copa.shut'));
    const hint = t('senda.copa.knotHint');
    const el = h(
      'button.al-stone.cp-offer',
      { type: 'button', 'data-test': 'winter-knot', 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.offerAt >= 0 && this.mark(this.offerAt)) },
      h('span.al-ring'),
      h('span.cp-glyph', { html: KNOT }),
      label,
    ) as HTMLButtonElement;
    setStyle(el, 'display', 'none');
    this.board.append(el);
    this.offerEl = el;
    this.offerLabel = label;
  }

  /** A tap on the board at a sim point: by a knot of the crown at work, its turn offered; true when it was. */
  offer(s: BattleState, x: number, y: number): boolean {
    const r = s.rules?.senda?.winter?.sap;
    const w = s.senda?.winter;
    if (!r || !w || w.done >= 0) return false;
    const best = knotNear(s, x, y, 1100);
    this.offerAt = best;
    if (best >= 0) {
      const p = r.knots[best] as { x: number; y: number };
      simToWorld(p.x, p.y, this.offerPoint, 0.9);
    }
    return best >= 0;
  }

  private set(id: string, text: string, cls?: 'warn' | 'good' | ''): void {
    const r = this.rows[id];
    if (!r) return;
    if (r.text.textContent !== text) r.text.textContent = text;
    r.row.classList.toggle('warn', cls === 'warn');
    r.row.classList.toggle('good', cls === 'good');
  }

  private hide(id: string, off: boolean): void {
    const r = this.rows[id];
    if (r && r.row.hidden !== off) r.row.hidden = off;
  }

  private secs(ticks: number): number {
    return Math.max(1, Math.ceil(ticks / TICK_HZ));
  }

  private updateBoughs(s: BattleState): void {
    const r = s.rules?.senda?.winter?.boughs;
    const w = s.senda?.winter;
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    if (!r || !w || !hero) return;
    // A block over the hero first; else the nearest coming down.
    let soon = -1;
    for (const b of w.blocks) {
      const over = ((b[0] as number) - hero.x) ** 2 + ((b[1] as number) - hero.y) ** 2 <= (r.snow.r + 200) ** 2;
      if (over) soon = soon < 0 ? (b[2] as number) : Math.min(soon, b[2] as number);
    }
    this.set('snow', soon >= 0 ? t('senda.copa.creak', { s: this.secs(soon - s.tick) }) : t('senda.copa.snow'), soon >= 0 ? 'warn' : '');
    // The next gap up from the hero: the nearest whose far end is above it.
    let gi = -1;
    let gd = Infinity;
    r.gaps.forEach((g, i) => {
      if (g.b.y > hero.y + 400) return;
      const d = (g.a.x - hero.x) ** 2 + (g.a.y - hero.y) ** 2;
      if (d < gd) {
        gd = d;
        gi = i;
      }
    });
    if (gi < 0) this.set('gap', t('senda.copa.gapNone'), 'good');
    else {
      const open = gapOpenAt(r.sway, gi, s.waveT);
      // Ticks to the change: walk the clock until it flips (a cycle at most).
      let k = 1;
      while (k < r.sway.every && gapOpenAt(r.sway, gi, s.waveT + k) === open) k++;
      this.set('gap', open ? t('senda.copa.gapOpen', { s: this.secs(k) }) : t('senda.copa.gapShut', { s: this.secs(k) }), open ? 'good' : '');
    }
    let crows = 0;
    for (const e of s.enemies) if (e.hp > 0 && e.kind === 'crow' && (e.x - hero.x) ** 2 + (e.y - hero.y) ** 2 < 3000 * 3000) crows++;
    this.set('crows', crows ? t('senda.copa.crows', { n: crows }) : t('senda.copa.crowsNone'), crows >= 3 ? 'warn' : '');
  }

  /** The swelling nearest to bursting: by whom and its seconds. */
  private updateSwell(s: BattleState): void {
    const w = s.senda?.winter;
    if (!w) return;
    let best: number[] | null = null;
    for (const id of Object.keys(w.sp)) {
      const m = w.sp[Number(id)] as number[];
      if (m[0] !== 1) continue;
      if (!best || (m[1] as number) < (best[1] as number)) best = m;
    }
    let under = 0;
    for (const id of Object.keys(w.sp)) if ((w.sp[Number(id)] as number[])[0] === 0) under++;
    if (best) {
      const left = this.secs((best[1] as number) - s.tick);
      const who = best[4] === 1 ? t('senda.copa.whoHob') : best[4] === 2 ? t('senda.copa.whoCeda') : '';
      this.set('swell', best[4] === 0 ? t('senda.copa.swellYou', { s: left }) : t('senda.copa.swellBy', { who, s: left }), 'warn');
    } else this.set('swell', t('senda.copa.serpents', { n: under }), '');
  }

  private updateHollow(s: BattleState): void {
    const r = s.rules?.senda?.winter?.hollow;
    const w = s.senda?.winter;
    if (!r || !w) return;
    const stopped = w.flinch > s.tick;
    this.set('bandage', stopped ? t('senda.copa.bandageStop') : t('senda.copa.bandage', { n: Math.floor((Math.min(w.work, r.need) * 100) / r.need) }), stopped ? 'warn' : w.done >= 0 ? 'good' : '');
    this.updateSwell(s);
  }

  private updateSap(s: BattleState): void {
    const r = s.rules?.senda?.winter?.sap;
    const w = s.senda?.winter;
    if (!r || !w || !w.closed.length) return;
    const crown = (stage: number): void => {
      const id = stage === 0 ? 'low' : 'high';
      if (w.stage > stage || w.done >= 0) return this.set(id, t('senda.copa.ran'), 'good');
      if (w.stage < stage) return this.set(id, t('senda.copa.frozen'), '');
      let open = 0;
      let cracked = 0;
      r.nodes.forEach((n, i) => {
        if (!n.need || n.stage !== stage) return;
        if (w.buds[i] === 0) open++;
        if (w.buds[i] === 1) cracked++;
      });
      const base = w.streak > 0 ? t('senda.copa.hold') : cracked ? `${t('senda.copa.buds', { n: open, need: r.need })} · ${t('senda.copa.cracked', { n: cracked })}` : t('senda.copa.buds', { n: open, need: r.need });
      this.set(id, base, w.streak > 0 ? 'good' : cracked ? 'warn' : '');
    };
    crown(0);
    crown(1);
    const boss = w.boss ? s.enemies.find((e) => e.id === w.boss) : undefined;
    if (w.claw >= 0) this.set('frost', t('senda.copa.claw', { s: this.secs(w.claw - s.tick) }), 'warn');
    else if (w.bwarn >= 0) this.set('frost', t('senda.copa.frostWarn', { s: this.secs(w.bwarn - s.tick) }), 'warn');
    else this.set('frost', boss && boss.hp > 0 ? t('senda.copa.frostAbout') : t('senda.copa.frostDown'), boss && boss.hp > 0 ? '' : 'good');
    this.updateSwell(s);
  }

  update(s: BattleState): void {
    if (this.kind === 'boughs') this.updateBoughs(s);
    else if (this.kind === 'hollow') this.updateHollow(s);
    else this.updateSap(s);
    // The turn on offer: gone once done; while the hero stands at it, its share; its word as the knot is now.
    const w = s.senda?.winter;
    const r = s.rules?.senda?.winter?.sap;
    const el = this.offerEl;
    if (!el || !w || !r) return;
    const job = w.job;
    if (job >= 0 && job !== this.offerAt) {
      this.offerAt = job;
      const p = r.knots[job];
      if (p) simToWorld(p.x, p.y, this.offerPoint, 0.9);
    }
    const stage = this.offerAt >= 0 ? (r.nodes[(r.knots[this.offerAt] as { node: number }).node] as { stage: number }).stage : -1;
    if (w.done >= 0 || s.result !== 'running' || (this.offerAt >= 0 && stage !== w.stage)) this.offerAt = -1;
    const sent = job >= 0 && job === this.offerAt;
    el.classList.toggle('sent', sent);
    el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.cut)).toFixed(2) : '0');
    const shut = this.offerAt >= 0 && w.closed[this.offerAt] === 1;
    const label = sent ? (w.held > 0 ? t('senda.copa.turning') : t('senda.copa.going')) : shut ? t('senda.copa.open') : t('senda.copa.shut');
    if (this.offerLabel && this.offerLabel.textContent !== label) this.offerLabel.textContent = label;
    el.classList.toggle('shut', shut);
    el.disabled = s.result !== 'running';
  }

  /** Every frame: the offer over its place. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    const el = this.offerEl;
    if (!el) return;
    if (this.offerAt < 0) {
      setStyle(el, 'display', 'none');
      return;
    }
    this.v.copy(this.offerPoint).project(camera);
    if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
      setStyle(el, 'display', 'none');
      return;
    }
    setStyle(el, 'display', '');
    pinMark(el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
  }
}
